/**
 * API service for making requests to the backend
 */
import axios from 'axios';

// Export the base URL for use in other files
export const API_BASE_URL = 'https://api.nobracinnovations.com/api';
export const SERVER_BASE_URL = 'https://api.nobracinnovations.com';

// Create an axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 15000, // Increased timeout to 15 seconds
  withCredentials: true, // Enable credentials for CORS
});

// Add request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${token}`;
      console.log('Request headers:', config.headers);
    } else {
      console.warn('No access token found in localStorage');
      // Only redirect if not already on login page and not trying to login
      if (window.location.pathname !== '/login' && !config.url.includes('/auth/login/')) {
        console.log('Redirecting to login page...');
        window.location.href = '/login';
      }
    }

    // Log the request for debugging
    console.log(`Making ${config.method.toUpperCase()} request to ${config.url}`, {
      headers: config.headers,
      data: config.data
    });

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Create an axios instance with extended timeout for file uploads
const fileUploadApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
  timeout: 30000, // 30 seconds timeout for file uploads
  withCredentials: true, // Enable credentials
});

// Add the same request interceptor to fileUploadApi
fileUploadApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      // Ensure headers object exists
      config.headers = config.headers || {};
      // Add Authorization header
      config.headers['Authorization'] = `Bearer ${token}`;
      console.log('File upload request headers:', config.headers);
    } else {
      console.warn('No access token found in localStorage for file upload');
    }

    return config;
  },
  (error) => {
    console.error('File upload request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
const responseInterceptor = (response) => {
  console.log('API Response:', response.config.url, response.data);
  return response.data;
};

const errorInterceptor = (error) => {
  console.error('API Error:', error);

  // Handle network errors
  if (!error.response) {
    console.error('Network error - server not responding');
    throw new Error('Unable to connect to the server. Please check if the server is running.');
  }

  // Handle authentication errors
  if (error.response && error.response.status === 401) {
    console.log('Authentication error - redirecting to login');
    // Clear auth data
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('farmconnect_user');
    localStorage.removeItem('farmconnect_role');
    localStorage.setItem('farmconnect_authenticated', 'false');
    
    // Redirect to login if not already there and not trying to login
    if (window.location.pathname !== '/login' && !error.config.url.includes('/auth/login/')) {
      window.location.href = '/login';
    }
  }

  // Extract the most useful error information
  let errorMessage = 'An unknown error occurred';
  let errorDetails = {};

  if (error.response) {
    console.error('Response error data:', error.response.data);

    if (typeof error.response.data === 'object') {
      errorDetails = error.response.data;

      if (error.response.data.detail) {
        errorMessage = error.response.data.detail;
      } else {
        const fieldErrors = Object.entries(error.response.data)
          .map(([field, errors]) => {
            const errorText = Array.isArray(errors) ? errors.join(', ') : errors;
            return `${field}: ${errorText}`;
          })
          .join('; ');

        if (fieldErrors) {
          errorMessage = `Validation error: ${fieldErrors}`;
        } else {
          errorMessage = `Error ${error.response.status}: ${error.response.statusText}`;
        }
      }
    } else if (typeof error.response.data === 'string') {
      errorMessage = error.response.data;
    } else {
      errorMessage = `Error ${error.response.status}: ${error.response.statusText}`;
    }
  }

  // Create an enhanced error object
  const enhancedError = new Error(errorMessage);
  enhancedError.details = errorDetails;
  enhancedError.status = error.response?.status;
  enhancedError.originalError = error;

  throw enhancedError;
};

// Apply interceptors to both API instances
api.interceptors.response.use(responseInterceptor, errorInterceptor);
fileUploadApi.interceptors.response.use(responseInterceptor, errorInterceptor);

/**
 * Farmer API functions
 */
export const farmerApi = {
  // Get all farmers
  getAllFarmers: async () => {
    try {
      const response = await api.get('/farmers/');
      return Array.isArray(response) ? response : [];
    } catch (error) {
      console.error('Error in getAllFarmers:', error);
      throw error;
    }
  },

  // Get a single farmer by ID
  getFarmer: async (id) => {
    try {
      const response = await api.get(`/farmers/${id}/`);
      return response;
    } catch (error) {
      console.error(`Error in getFarmer(${id}):`, error);
      throw error;
    }
  },

  // Get CO2 emissions data for a farmer
  getFarmerEmissions: async (id) => {
    try {
      console.log('Fetching emissions for farmer:', id);
      const response = await api.get(`/farmers/${id}/emissions/`);
      console.log('Emissions API response:', response);
      return response;
    } catch (error) {
      console.error('Error in getFarmerEmissions:', error);
      throw error;
    }
  },

  // Create a new farmer
  createFarmer: async (data) => {
    try {
      console.log('Creating farmer with data:', data);
      // Ensure axios automatically sets the Content-Type header for FormData
      const response = await api.post('/farmers/', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('createFarmer response:', response);
      return response;
    } catch (error) {
      console.error('Error in createFarmer:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }
      throw error;
    }
  },

  // Update a farmer
  updateFarmer: async (id, data) => { // data is expected to be a FormData object
    try {
      const response = await api.put(`/farmers/${id}/`, data, {
        headers: {
          // Explicitly set Content-Type for FormData. Axios should do this automatically
          // but setting it explicitly can help avoid issues if defaults interfere.
          'Content-Type': 'multipart/form-data',
        },
      });
      return response;
    } catch (error) {
      console.error(`Error in updateFarmer(${id}):`, error);
      if (error.response && error.response.data) {
        // To inspect FormData content, you'd typically log it on the frontend
        // before sending, as 'data' here will just log as an empty object.
        console.error('Backend validation error on update:', error.response.data);
      }
      throw error;
    }
  },

  // Delete a farmer
  deleteFarmer: async (id) => {
    try {
      await api.delete(`/farmers/${id}/`);
    } catch (error) {
      console.error(`Error in deleteFarmer(${id}):`, error);
      throw error;
    }
  },

  // Upload media files
  uploadMedia: async (file, farmerId, mediaType) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('farmer_id', farmerId);
      formData.append('media_type', mediaType);

      const response = await fileUploadApi.post('/farmers/media/upload/', formData);
      console.log('uploadMedia response:', response);
      return response;
    } catch (error) {
      console.error('Error in uploadMedia:', error);
      throw error;
    }
  }
};

/**
 * Mapping API functions
 */
export const mappingApi = {
  // Get all mappings
  getAllMappings: async () => {
    try {
      console.log('Fetching all mappings...');
      const response = await api.get('/farmer-mappings/');
      console.log('Raw mappings response:', response);
      
      // Handle different response formats
      let mappings = [];
      if (Array.isArray(response)) {
        mappings = response;
      } else if (response && response.results && Array.isArray(response.results)) {
        mappings = response.results;
      } else if (response && typeof response === 'object') {
        // Try to find an array in the response
        for (const key in response) {
          if (Array.isArray(response[key])) {
            mappings = response[key];
            break;
          }
        }
      }
      
      // Ensure each mapping has the required fields
      return mappings.map(mapping => ({
        id: mapping.id,
        company: typeof mapping.company === 'object' ? mapping.company.id : mapping.company,
        farmer: typeof mapping.farmer === 'object' ? mapping.farmer.id : mapping.farmer,
        status: mapping.status || 'active',
        created_at: mapping.created_at,
        updated_at: mapping.updated_at
      }));
    } catch (error) {
      console.error('Error in getAllMappings:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
      }
      throw error;
    }
  },

  // Get a single mapping by ID
  getMapping: async (id) => {
    try {
      const response = await api.get(`/farmer-mappings/${id}/`);
      return response;
    } catch (error) {
      console.error(`Error in getMapping(${id}):`, error);
      throw error;
    }
  },

  // Get mappings for a company
  getCompanyMappings: async (companyId) => {
    try {
      console.log('Fetching mappings for company:', companyId);
      const response = await api.get(`/farmer-mappings/?company=${companyId}`);
      console.log('Raw company mappings response:', response);
      
      // Handle different response formats
      let mappings = [];
      if (Array.isArray(response)) {
        mappings = response;
      } else if (response && response.results && Array.isArray(response.results)) {
        mappings = response.results;
      } else if (response && typeof response === 'object') {
        // Try to find an array in the response
        for (const key in response) {
          if (Array.isArray(response[key])) {
            mappings = response[key];
            break;
          }
        }
      }
      
      // Ensure each mapping has the required fields
      return mappings.map(mapping => ({
        id: mapping.id,
        company: typeof mapping.company === 'object' ? mapping.company.id : mapping.company,
        farmer: typeof mapping.farmer === 'object' ? mapping.farmer.id : mapping.farmer,
        status: mapping.status || 'active',
        created_at: mapping.created_at,
        updated_at: mapping.updated_at
      }));
    } catch (error) {
      console.error(`Error in getCompanyMappings(${companyId}):`, error);
      if (error.response) {
        console.error('Error response:', error.response.data);
      }
      throw error;
    }
  },

  // Get mappings for a farmer
  getFarmerMappings: async (farmerId) => {
    try {
      const response = await api.get(`/farmer-mappings/?farmer=${farmerId}`);
      return response;
    } catch (error) {
      console.error(`Error in getFarmerMappings(${farmerId}):`, error);
      throw error;
    }
  },

  // Check if mapping exists
  checkMappingExists: async (companyId, farmerId) => {
    try {
      const response = await api.get(`/farmer-mappings/?company=${companyId}&farmer=${farmerId}`);
      return Array.isArray(response) ? response.length > 0 : false;
    } catch (error) {
      console.error('Error checking mapping:', error);
      return false;
    }
  },

  // Create a new mapping
  createMapping: async (data) => {
    try {
      console.log('API - Creating mapping with data:', data);
      
      // Validate required fields
      if (!data.company_id || !data.farmer_id) {
        throw new Error('Company ID and Farmer ID are required');
      }

      // Create the mapping with the correct field names
      const mappingData = {
        company: data.company_id,
        farmer: data.farmer_id,
        status: data.status || 'active'
      };

      console.log('API - Sending mapping data:', mappingData);
      const response = await api.post('/farmer-mappings/', mappingData);
      console.log('API - Mapping created successfully:', response);
      return { created: [response] };
    } catch (error) {
      console.error('API - Error creating mapping:', error);
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      if (error.response?.data?.non_field_errors) {
        throw new Error(error.response.data.non_field_errors.join(', '));
      }
      throw error;
    }
  },

  // Update a mapping
  updateMapping: async (id, mappingData) => {
    try {
      const response = await api.patch(`/farmer-mappings/${id}/`, mappingData);
      return response;
    } catch (error) {
      console.error(`Error in updateMapping(${id}):`, error);
      throw error;
    }
  },

  // Delete a mapping
  deleteMapping: async (id) => {
    try {
      await api.delete(`/farmer-mappings/${id}/`);
    } catch (error) {
      console.error(`Error in deleteMapping(${id}):`, error);
      throw error;
    }
  }
};

/**
 * External APIs
 */
export const externalApi = {
  // Get pincode data from the postal API
  getPincodeData: async (pincode) => {
    try {
      const response = await axios.get(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = response.data;

      if (data && data[0] && data[0].Status === 'Success' && data[0].PostOffice && data[0].PostOffice.length > 0) {
        const postOffice = data[0].PostOffice[0];
        return {
          district: postOffice.District || '',
          state: postOffice.State || ''
        };
      } else {
        console.log('No data found for pincode:', pincode);
        return null;
      }
    } catch (error) {
      console.error('Error fetching pincode data:', error);
      return null;
    }
  }
};

/**
 * Volunteer API functions
 */
export const volunteerApi = {
  // Get all volunteers
  getAllVolunteers: () => api.get('/volunteers/'),

  // Get a single volunteer by ID
  getVolunteer: (id) => api.get(`/volunteers/${id}/`),

  // Create a new volunteer
  createVolunteer: async (volunteerData) => {
    try {
      console.log('Creating volunteer with data:', volunteerData);
      const response = await api.post('/volunteers/', volunteerData);
      console.log('Volunteer creation response:', response);

      // The response interceptor already processes the response
      // So we can return the data directly
      return response;
    } catch (error) {
      console.error('Error creating volunteer:', error);
      
      // Enhance error message
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.detail) {
          error.message = errorData.detail;
        } else if (errorData.email) {
          error.message = `Email error: ${errorData.email}`;
        } else if (errorData.password) {
          error.message = `Password error: ${errorData.password}`;
        } else if (typeof errorData === 'object') {
          const errorMessages = Object.entries(errorData)
            .map(([field, value]) => `${field}: ${value}`)
            .join(', ');
          error.message = errorMessages;
        }
      }
      
      throw error;
    }
  },

  // Update a volunteer
  updateVolunteer: async (id, volunteerData) => {
    try {
      // Only send password if it's present and not empty
      const dataToSend = { ...volunteerData };
      if (!dataToSend.password) delete dataToSend.password;
      if (!dataToSend.status) delete dataToSend.status;
      
      console.log('Sending update request with data:', dataToSend);
      const response = await api.patch(`/volunteers/${id}/`, dataToSend);
      console.log('Volunteer update response:', response);
      return response;
    } catch (error) {
      console.error('Error updating volunteer:', error);
      throw error;
    }
  },

  // Delete a volunteer
  deleteVolunteer: async (id) => {
    try {
      console.log('Sending delete request for volunteer:', id);
      const response = await api.delete(`/volunteers/${id}/`);
      console.log('Volunteer deletion response:', response);
      return response;
    } catch (error) {
      console.error('Error deleting volunteer:', error);
      throw error;
    }
  }
};

/**
 * Company API functions
 */
export const companyApi = {
  // Get all companies
  getAllCompanies: async () => {
    try {
      const response = await api.get('/companies/');
      return {
        companies: Array.isArray(response.companies) ? response.companies : [],
        count: response.count || 0
      };
    } catch (error) {
      console.error('Error in getAllCompanies:', error);
      throw error;
    }
  },

  // Get a single company by ID
  getCompany: async (id) => {
    try {
      const response = await api.get(`/companies/${id}/`);
      console.log(`getCompany(${id}) response:`, response);
      return response;
    } catch (error) {
      console.error(`Error in getCompany(${id}):`, error);
      throw error;
    }
  },

  // Create a new company
  createCompany: async (companyData) => {
    console.log('Creating company with data:', companyData);
    try {
      const response = await api.post('/companies/', companyData);
      console.log('createCompany response:', response);
      return response;
    } catch (error) {
      console.error('Error in createCompany:', error);
      console.error('Error response:', error.response);
      throw error;
    }
  },

  // Update a company
  updateCompany: async (id, companyData) => {
    try {
      const response = await api.patch(`/companies/${id}/`, companyData);
      console.log(`updateCompany(${id}) response:`, response);
      return response;
    } catch (error) {
      console.error(`Error in updateCompany(${id}):`, error);
      throw error;
    }
  },

  // Delete a company
  deleteCompany: async (id) => {
    try {
      const response = await api.delete(`/companies/${id}/`);
      console.log(`deleteCompany(${id}) response:`, response);
      return response;
    } catch (error) {
      console.error(`Error in deleteCompany(${id}):`, error);
      throw error;
    }
  },
};

/**
 * Authentication API functions
 */
export const authApi = {
  // Login user
  login: async (username, password, role) => {
    try {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(username)) {
        throw new Error('Invalid email format');
      }

      console.log('Attempting login with:', { email: username, role });

      // Clear any existing auth data first
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('farmconnect_user');
      localStorage.removeItem('farmconnect_role');
      localStorage.setItem('farmconnect_authenticated', 'false');

      // Prepare login data
      const loginData = {
        email: username,
        password: password,
        role: role || 'admin'
      };

      console.log('Sending login request with data:', { ...loginData, password: '****' });

      // Make the login request
      const response = await api.post('/auth/login/', loginData);
      console.log('Login API raw response:', response);

      // Handle different response formats
      let userData, accessToken, refreshToken, dashboardUrl;

      if (response.data) {
        // If response has data property
        console.log('Response has data property:', response.data);
        userData = response.data.user || response.data;
        accessToken = response.data.access || response.data.token;
        refreshToken = response.data.refresh;
        dashboardUrl = response.data.dashboard_url;
      } else {
        // If response is the data itself
        console.log('Response is the data itself:', response);
        userData = response.user || response;
        accessToken = response.access || response.token;
        refreshToken = response.refresh;
        dashboardUrl = response.dashboard_url;
      }

      console.log('Extracted user data:', userData);
      console.log('User role:', userData.role || role);

      // Validate required data
      if (!accessToken) {
        throw new Error('No access token received from server');
      }

      // Store tokens in localStorage
      localStorage.setItem('access_token', accessToken);
      console.log('Access token stored:', accessToken.substring(0, 20) + '...');

      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }

      // Store user data
      const userRole = userData.role || role || 'admin';
      
      // Ensure company data is properly structured
      if (userRole === 'company') {
        console.log('Processing company user data in API service');
        console.log('Raw company data:', userData.company);
        
        // Try to get company ID from various possible locations
        if (userData.company) {
          if (typeof userData.company === 'object') {
            console.log('Company is an object:', userData.company);
            userData.companyId = userData.company.id;
          } else {
            console.log('Company is a direct value:', userData.company);
            userData.companyId = userData.company;
          }
        } else if (userData.company_id) {
          console.log('Found company_id:', userData.company_id);
          userData.companyId = userData.company_id;
        } else if (userData.companyId) {
          console.log('Found companyId:', userData.companyId);
          userData.companyId = userData.companyId;
        }

        console.log('Final company user data:', userData);
      }

      localStorage.setItem('farmconnect_user', JSON.stringify(userData));
      localStorage.setItem('farmconnect_role', userRole);
      localStorage.setItem('farmconnect_authenticated', 'true');

      // Verify token is stored
      const storedToken = localStorage.getItem('access_token');
      if (!storedToken) {
        console.error('Failed to store access token');
        throw new Error('Failed to store authentication token');
      }

      console.log('Login successful, user data stored');

      return {
        success: true,
        user: userData,
        role: userRole,
        dashboard_url: dashboardUrl
      };
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response);

      // Clear any existing auth data
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('farmconnect_user');
      localStorage.removeItem('farmconnect_role');
      localStorage.setItem('farmconnect_authenticated', 'false');

      // Extract error message from response
      let errorMessage = 'Login failed';
      if (error.response && error.response.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.non_field_errors) {
          errorMessage = error.response.data.non_field_errors.join(', ');
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  },

  // Register new user
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register/', userData);

      // Store tokens in localStorage
      if (response.data && response.data.access) {
        localStorage.setItem('access_token', response.data.access);
      }
      if (response.data && response.data.refresh) {
        localStorage.setItem('refresh_token', response.data.refresh);
      }

      return {
        success: true,
        user: response.data.user
      };
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Get user profile
  getProfile: async () => {
    try {
      const response = await api.get('/auth/profile/');
      return {
        success: true,
        user: response
      };
    } catch (error) {
      console.error('Get profile error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Update user profile
  updateProfile: async (userData) => {
    try {
      const response = await api.patch('/auth/profile/', userData);
      return {
        success: true,
        user: response
      };
    } catch (error) {
      console.error('Update profile error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Get company profile
  getCompanyProfile: async () => {
    try {
      const response = await api.get('/auth/company/profile/');
      return {
        success: true,
        profile: response
      };
    } catch (error) {
      console.error('Get company profile error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Update company profile
  updateCompanyProfile: async (profileData) => {
    try {
      const response = await api.patch('/auth/company/profile/', profileData);
      return {
        success: true,
        profile: response
      };
    } catch (error) {
      console.error('Update company profile error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Logout user
  logout: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }
};

export default {
  farmerApi,
  mappingApi,
  externalApi,
  volunteerApi,
  companyApi,
  authApi,
};

